import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import * as api from '../services/api';

const DataContext = createContext();

export const useData = () => useContext(DataContext);

// Default Mock Data (fallback when API is unavailable)
const defaultReleases = [
  {
    id: "1",
    title: "Forest Whispers",
    year: "2024",
    type: "EP",
    cover: "https://images.unsplash.com/photo-1661539971389-5ce412c72311?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxtYW5kYWxhJTIwZ2xvd2luZ3xlbnwwfHx8fDE3NjM4NTU1MTd8MA&ixlib=rb-4.1.0&q=85"
  },
  {
    id: "2",
    title: "Sacred Geometry",
    year: "2023",
    type: "Single",
    cover: "https://images.pexels.com/photos/1029624/pexels-photo-1029624.jpeg"
  },
  {
    id: "3",
    title: "Dharmar Chronicles Vol. 1",
    year: "2023",
    type: "Compilation",
    cover: "https://images.unsplash.com/photo-1619879310659-01e83a8e9d6b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxzYWNyZWQlMjBnZW9tZXRyeXxlbnwwfHx8fDE3NjM4NTU1MDZ8MA&ixlib=rb-4.1.0&q=85"
  }
];

const defaultAboutContent = {
  title: "The Artist",
  heading: "Channeling the Old School Spirit",
  mainImage: "https://images.unsplash.com/photo-1698153763051-06fcad199e24?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxkaiUyMGRhcmt8ZW58MHx8fHwxNzYzODU1NTEyfDA&ixlib=rb-4.1.0&q=85",
  paragraph1: "Hailing from Belgrade, Serbia, Korov stands as a guardian of the authentic Psytrance sound. As a core member of the Dharmar organization, he has dedicated his craft to preserving the raw, hypnotic energy that defined the genre's golden era.",
  paragraph2: "His sets are not just musical performances but ritualistic journeys—weaving through dark forests, sacred geometries, and cosmic vibrations. Korov doesn't just play tracks; he constructs atmospheres where the past and future of trance collide.",
  stat1Value: "10+",
  stat1Label: "Years Active",
  stat2Value: "Dharmar",
  stat2Label: "Label Home"
};

const defaultEvents = [
  {
    id: '1',
    title: 'Dharmar Night: Old School Revival',
    date: '2025-01-15',
    time: '23:00',
    venue: 'Club Terminus, Belgrade',
    description: 'Join us for a night of pure old-school psytrance energy with Korov headlining.',
    imageUrl: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=800',
    ticketLink: '#'
  },
  {
    id: '2',
    title: 'Forest Gathering 2025',
    date: '2025-02-20',
    time: '22:00',
    venue: 'Secret Location, Serbia',
    description: 'Underground forest party featuring the best of Serbian psytrance scene.',
    imageUrl: 'https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?w=800',
    ticketLink: '#'
  }
];

const defaultMessages = [];

export const DataProvider = ({ children }) => {
  const [releases, setReleases] = useState(defaultReleases);
  const [aboutContent, setAboutContent] = useState(defaultAboutContent);
  const [events, setEvents] = useState(defaultEvents);
  const [messages, setMessages] = useState(defaultMessages);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [apiAvailable, setApiAvailable] = useState(true);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      setAuthLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setIsAuthenticated(false);
        setAuthLoading(false);
        return;
      }
      
      try {
        const isValid = await api.verifyToken();
        setIsAuthenticated(isValid);
      } catch (error) {
        console.warn('Token verification failed:', error);
        setIsAuthenticated(false);
      } finally {
        setAuthLoading(false);
      }
    };
    checkAuth();
  }, []);

  // Fetch initial data from API
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [releasesData, eventsData, aboutData] = await Promise.all([
        api.getReleases().catch(() => null),
        api.getEvents().catch(() => null),
        api.getAbout().catch(() => null),
      ]);

      if (releasesData !== null) {
        setReleases(releasesData.length > 0 ? releasesData : defaultReleases);
      }
      if (eventsData !== null) {
        setEvents(eventsData.length > 0 ? eventsData : defaultEvents);
      }
      if (aboutData !== null) {
        setAboutContent(aboutData);
      }
      setApiAvailable(true);
    } catch (error) {
      console.warn('API unavailable, using default data:', error);
      setApiAvailable(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch messages (requires auth)
  const fetchMessages = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const messagesData = await api.getMessages();
      setMessages(messagesData || []);
    } catch (error) {
      console.warn('Failed to fetch messages:', error);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchMessages();
    }
  }, [fetchMessages, isAuthenticated]);

  // Auth actions
  const login = async (username, password) => {
    try {
      await api.login(username, password);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    api.logout();
    setIsAuthenticated(false);
    setMessages([]);
  };

  // Release actions
  const addRelease = async (release) => {
    try {
      const newRelease = await api.createRelease(release);
      setReleases(prev => [...prev, newRelease]);
      toast.success("Release added successfully");
      return newRelease;
    } catch (error) {
      toast.error(error.message || "Failed to add release");
      throw error;
    }
  };

  const updateRelease = async (id, releaseData) => {
    try {
      const updated = await api.updateRelease(id, releaseData);
      setReleases(prev => prev.map(r => r.id === id ? updated : r));
      toast.success("Release updated successfully");
      return updated;
    } catch (error) {
      toast.error(error.message || "Failed to update release");
      throw error;
    }
  };

  const deleteRelease = async (id) => {
    try {
      await api.deleteRelease(id);
      setReleases(prev => prev.filter(r => r.id !== id));
      toast.success("Release deleted");
    } catch (error) {
      toast.error(error.message || "Failed to delete release");
      throw error;
    }
  };

  // About actions
  const updateAboutContent = async (content) => {
    try {
      await api.updateAbout(content);
      setAboutContent(content);
      toast.success("About page updated successfully");
    } catch (error) {
      toast.error(error.message || "Failed to update about page");
      throw error;
    }
  };

  // Event actions
  const addEvent = async (event) => {
    try {
      const newEvent = await api.createEvent(event);
      setEvents(prev => [...prev, newEvent]);
      toast.success("Event added successfully");
      return newEvent;
    } catch (error) {
      toast.error(error.message || "Failed to add event");
      throw error;
    }
  };

  const updateEvent = async (id, eventData) => {
    try {
      const updated = await api.updateEvent(id, eventData);
      setEvents(prev => prev.map(e => e.id === id ? updated : e));
      toast.success("Event updated successfully");
      return updated;
    } catch (error) {
      toast.error(error.message || "Failed to update event");
      throw error;
    }
  };

  const deleteEvent = async (id) => {
    try {
      await api.deleteEvent(id);
      setEvents(prev => prev.filter(e => e.id !== id));
      toast.success("Event deleted");
    } catch (error) {
      toast.error(error.message || "Failed to delete event");
      throw error;
    }
  };

  // Message actions (public create via contact form)
  const addMessage = async (msg) => {
    try {
      const newMessage = await api.createMessage(msg);
      // Only add to state if admin is viewing
      if (isAuthenticated) {
        setMessages(prev => [...prev, newMessage]);
      }
      return newMessage;
    } catch (error) {
      throw error;
    }
  };

  const updateMessageStatus = async (id, status) => {
    try {
      await api.updateMessageStatus(id, status);
      setMessages(prev => prev.map(m => m.id === id ? { ...m, status } : m));
      toast.success(`Message marked as ${status}`);
    } catch (error) {
      toast.error(error.message || "Failed to update message status");
      throw error;
    }
  };

  const deleteMessage = async (id) => {
    try {
      await api.deleteMessage(id);
      setMessages(prev => prev.filter(m => m.id !== id));
      toast.success("Message deleted");
    } catch (error) {
      toast.error(error.message || "Failed to delete message");
      throw error;
    }
  };

  // Refresh data from API
  const refreshData = useCallback(async () => {
    await fetchData();
    if (isAuthenticated) {
      await fetchMessages();
    }
  }, [fetchData, fetchMessages, isAuthenticated]);

  return (
    <DataContext.Provider value={{
      releases,
      aboutContent,
      events,
      messages,
      isAuthenticated,
      authLoading,
      loading,
      apiAvailable,
      login,
      logout,
      addRelease,
      updateRelease,
      deleteRelease,
      updateAboutContent,
      addEvent,
      updateEvent,
      deleteEvent,
      addMessage,
      updateMessageStatus,
      deleteMessage,
      refreshData
    }}>
      {children}
    </DataContext.Provider>
  );
};
